import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Bookmark, Coins, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { VerifyBanner } from "@/components/app/VerifyBanner";
import { SmartImg } from "@/components/site/SmartImg";
import { useAuth } from "@/context/AuthContext";
import { libApi } from "@/lib/api";
import { LIBRARY } from "@/constants/testIds";
import catalog from "@/data/payments-copy.json";

// Mapowanie kategorii katalogu (slug) na istniejące grafiki ze strony głównej.
const CATEGORY_IMAGES = {
  "nieruchomosci": "/assets/packages/real-estate-interiors.png",
  "beauty-kosmetyka-salony": "/assets/packages/beauty-fashion-salons.png",
  "budownictwo-uslugi-lokalne": "/assets/packages/construction-local-services.png",
  "produkty-e-commerce": "/assets/packages/products-ecommerce.png",
  "postacie-ai-influencerzy": "/assets/packages/ai-influencers.png",
  "marka-osobista-eksperci": "/assets/packages/personal-brand-experts.png",
  "zwierzeta-branza-zoologiczna": "/assets/packages/pets.png",
  "gastronomia": "/assets/packages/gastronomy.png",
};

function Skeleton() {
  return <div className="h-40 animate-pulse rounded-panel border border-brand-line bg-brand-surface" />;
}

// Narzędzia Promptivo — te same grafiki/opisy co na publicznej stronie głównej.
const TOOLS = [
  {
    key: "cv", to: "/cv", img: "/assets/nowosci/promptivo-cv-feature.svg",
    alt: "Podgląd CV, wskaźnika dopasowania do oferty i listy potencjalnych pracodawców.",
    title: "CV gotowe na współczesną rekrutację",
    desc: "Stwórz CV od zera lub popraw obecne. Dopasuj je do konkretnej oferty, sprawdź zgodność z wymaganiami i przygotuj dokument czytelny dla systemów ATS oraz rekrutera. Znajdź też firmy, do których warto aplikować.",
    cta: "Stwórz lub sprawdź CV",
  },
  {
    key: "business", to: "/biznes/generator", img: "/assets/nowosci/promptivo-business-feature.svg",
    alt: "Trzy pomysły biznesowe ze scoringiem i siedmiodniowym planem walidacji.",
    title: "Znajdź pomysł na biznes dopasowany do Ciebie",
    desc: "Odpowiedz na kilka pytań, a Promptivo wygeneruje 3 realne kierunki dopasowane do Twoich umiejętności, budżetu i czasu — ze scoringiem oraz planem pierwszych działań.",
    cta: "Wygeneruj pomysły",
  },
];

export default function Library() {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, packages: [], error: false });

  useEffect(() => {
    let active = true;
    libApi
      .library()
      .then((d) => active && setState({ loading: false, packages: d.packages || [], error: false }))
      .catch(() => active && setState({ loading: false, packages: [], error: true }));
    return () => {
      active = false;
    };
  }, []);

  const firstName = user?.first_name;
  const greeting = firstName ? `Dzień dobry, ${firstName}.` : "Dzień dobry.";
  const c = catalog.catalog;

  return (
    <AppShell>
      <div data-testid={LIBRARY.root} className="mx-auto max-w-content">
        <VerifyBanner />

        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-accent">Twoja przestrzeń</p>
        <h1 className="mt-2 font-display text-3xl font-semibold text-brand-text sm:text-4xl">{greeting}</h1>
        <p className="mt-3 max-w-2xl text-brand-sec">{c.subtitle}</p>

        {/* Credit access info */}
        <div className="mt-6 flex flex-col items-start gap-4 rounded-panel border border-brand-accent/30 bg-brand-surface p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
              <Coins size={18} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="font-display text-lg font-semibold text-brand-text">{c.creditInfoTitle}</h2>
              <p className="text-sm text-brand-sec">{c.creditInfoBody}</p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-brand-accent/10 px-3 py-1 text-xs font-semibold text-brand-accent">
            {c.noExpiry}
          </span>
        </div>

        {/* Narzędzia Promptivo — kafle NOWOŚĆ (widoczne niezależnie od salda i weryfikacji e-mail) */}
        <section className="mt-10" data-testid="library-tools-section">
          <h2 className="font-display text-2xl font-semibold text-brand-text">Narzędzia Promptivo</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
            {TOOLS.map((t) => (
              <Link
                key={t.key}
                to={t.to}
                data-testid={`library-tool-${t.key}`}
                className="group flex h-full flex-col overflow-hidden rounded-panel border border-brand-line bg-brand-surface outline-none transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-brand-accent/50 focus-visible:border-brand-accent focus-visible:ring-2 focus-visible:ring-brand-accent/60"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-brand-bg">
                  <img src={t.img} alt={t.alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <span className="absolute left-3 top-3 rounded-full bg-brand-bg/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-accent backdrop-blur">Nowość</span>
                </div>
                <div className="flex flex-1 flex-col gap-3 p-6">
                  <h3 className="font-display text-xl font-semibold text-brand-text">{t.title}</h3>
                  <p className="text-[15px] leading-relaxed text-brand-sec">{t.desc}</p>
                  <span
                    data-testid={`library-tool-${t.key}-cta`}
                    className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-3 text-sm font-semibold text-brand-on transition-transform group-hover:translate-y-[-1px] sm:w-auto sm:justify-start sm:bg-transparent sm:px-0 sm:py-0 sm:text-brand-accent"
                  >
                    {t.cta} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Catalog grid */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold text-brand-text">Kategorie promptów</h2>
          {state.loading ? (
            <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          ) : (
            <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {state.packages.map((pkg) => (
                <Link
                  key={pkg.slug}
                  to={`/biblioteka/${pkg.slug}`}
                  data-testid={`${LIBRARY.packageCard}-${pkg.slug}`}
                  className="hover-glow group flex flex-col overflow-hidden rounded-panel border border-brand-line bg-brand-surface transition-colors hover:border-brand-accent/50"
                >
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-brand-elevated">
                    <SmartImg
                      src={CATEGORY_IMAGES[pkg.slug] || "/assets/packages/real-estate-interiors.png"}
                      alt={pkg.name}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.04]"
                      data-testid={`library-category-image-${pkg.slug}`}
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-brand-bg/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-accent backdrop-blur">
                      {c.cardLabel}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-semibold text-brand-text">{pkg.name}</h3>
                    <p className="mt-1.5 flex-1 text-sm text-brand-sec line-clamp-2">{pkg.short_description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm text-brand-muted">{pkg.prompt_count} promptów</span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-accent">
                        {c.cardCta} <ArrowRight size={15} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Quick access */}
        <section className="mt-12">
          <h2 className="font-display text-xl font-semibold text-brand-text">Szybki dostęp</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-3">
            <Link to="/dopasuj-prompt" className="hover-glow rounded-card border border-brand-line bg-brand-surface p-6 transition-colors hover:border-brand-accent/50">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-accent">
                <Sparkles size={14} strokeWidth={2} /> Dopasuj prompt
              </div>
              <h3 className="mt-3 font-display text-base font-semibold text-brand-text">Nie wiesz, od czego zacząć?</h3>
              <p className="mt-1.5 text-sm text-brand-sec">Odpowiedz na 3 pytania — dobierzemy prompt.</p>
            </Link>
            <Link to="/ulubione" className="hover-glow rounded-card border border-brand-line bg-brand-surface p-6 transition-colors hover:border-brand-accent/50">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-accent">
                <Heart size={14} strokeWidth={2} /> Ulubione
              </div>
              <h3 className="mt-3 font-display text-base font-semibold text-brand-text">Ulubione prompty</h3>
              <p className="mt-1.5 text-sm text-brand-sec">Zapisane szablony zawsze pod ręką.</p>
            </Link>
            <Link to="/zapisane-prompty" data-testid="dashboard-saved-shortcut" className="hover-glow rounded-card border border-brand-line bg-brand-surface p-6 transition-colors hover:border-brand-accent/50">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-accent">
                <Bookmark size={14} strokeWidth={2} /> Zapisane
              </div>
              <h3 className="mt-3 font-display text-base font-semibold text-brand-text">Twoje gotowe teksty</h3>
              <p className="mt-1.5 text-sm text-brand-sec">Wróć do promptów zapisanych w kreatorze.</p>
            </Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
